import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { bindActionCreators } from 'redux';

import LogoPandoraIcon from '../../../../static/icons/logo_pandora.svg';
import Rings from '../../../../static/icons/station_rings.svg';
import * as Constants from '../../../constants';
import { ContentSections } from '../../../constants/ContentSections';
import QA from '../../../constants/QATags';
import { AsyncDomain } from '../../../domains';
import { getInDomain } from '../../../domains/externalUtils';
import { isMobileWeb, showLogoOnlyHeader } from '../../../lib/CapabilitiesUtils';
import { AppContextIntegrationData } from '../../../lib/Context';
import {
  getCurrentPathnameFromState,
  getMobileLongLink,
  isBackstageChildPath,
  isBackstageParentPath,
} from '../../../lib/RouteUtils';
import { createBEM } from '../../../lib/utils/css';
import * as Utils from '../../../utils';
import Footer from '../../Footer/Footer';
import Icon from '../../Icon/Icon';
import Img from '../../ImageLoader/ImageLoader';
import { withVisibilityLogger } from '../../VisibilityLogger/VisibilityLogger';
import BgSetter from '../BgSetter';
import { HeaderBackstageWithLogger } from '../HeaderBackstage/HeaderBackstage';

import './BackstageLayout.scss';

const HeroImageWithLogger = withVisibilityLogger({
  contentSection: ContentSections.HERO_IMAGE,
})(Img);

export class BackstageLayout extends React.Component {
  static propTypes = {
    currentPathname: PropTypes.string,
    // Original backstage pages used an seoToken parsed from the URL. Newer pages use the pandoraId associated with an entity
    // The `entityGetterFunc` will be called with `entityIdentifier` to retrieve data to render.
    entityGetterFunc: PropTypes.func,
    entityIdentifier: PropTypes.string,
    hasCuratorPrograms: PropTypes.bool,
    headerProps: PropTypes.object,
    heroImageHasRings: PropTypes.bool,
    heroImageIsCircle: PropTypes.bool,
    heroImageLinkUrl: PropTypes.string,
    heroImageUrl: PropTypes.string,
    isHeaderInSidebar: PropTypes.bool,
    isMobileWeb: PropTypes.bool,
    isRightRailActive: PropTypes.bool,
    mobileLongLink: PropTypes.string,
    pandoraId: PropTypes.string,
    playOnClick: PropTypes.func,
    renderArtworkPlayButton: PropTypes.bool,
    renderMainContent: PropTypes.func.isRequired,
    renderSidebarButtons: PropTypes.func,
    renderStructuredMetaData: PropTypes.func,
    showedBackstagePage: PropTypes.func.isRequired,
    showLogoOnlyHeader: PropTypes.bool,
  };

  componentDidMount() {
    this.fetchData();
    this.logViewed();
  }

  componentDidUpdate(prevProps) {
    if (
      isBackstageParentPath(this.props.currentPathname) &&
      this.props.entityIdentifier !== prevProps.entityIdentifier
    ) {
      Utils.Scroll.scrollToTop();
      this.fetchData();
    }
    if (
      (this.props.currentPathname !== prevProps.currentPathname &&
        isBackstageChildPath(this.props.currentPathname)) ||
      this.props.entityIdentifier !== prevProps.entityIdentifier
    ) {
      this.logViewed();
    }
  }

  fetchData = () => {
    const entityIdentifier = this.props.entityIdentifier;
    const entityGetterFunc = this.props.entityGetterFunc;
    if (entityIdentifier && entityGetterFunc) {
      entityGetterFunc(entityIdentifier);
    }
  };

  logViewed = () => {
    const entityIdentifier = this.props.entityIdentifier;
    if (entityIdentifier) {
      this.props.showedBackstagePage({
        pageId: entityIdentifier,
        hasCuratorPrograms: this.props.hasCuratorPrograms,
      });
    }
  };

  getIntegrationData = () => {
    return {
      pandoraId: this.props.pandoraId,
      entityIdentifier: this.props.entityIdentifier,
    };
  };

  render() {
    const classes = createBEM('BackstageLayout', {
      rightRail: this.props.isRightRailActive,
      hasHeaderLogo: this.props.showLogoOnlyHeader,
    });

    return (
      <AppContextIntegrationData data={this.getIntegrationData}>
        <div className={classes} data-qa={QA.BS}>
          {this.props.renderStructuredMetaData && this.props.renderStructuredMetaData()}
          {this.props.showLogoOnlyHeader && (
            <a href={this.props.mobileLongLink} className='BackstageLayout__logo'>
              <Icon className='BackstageLayout__logo__icon' glyph={LogoPandoraIcon} />
            </a>
          )}
          <div className='BackstageLayout__body'>
            {(!this.props.isHeaderInSidebar || this.props.isMobileWeb) && (
              <header className='BackstageLayout__body__header'>
                <HeaderBackstageWithLogger {...this.props.headerProps} />
              </header>
            )}
            <aside className='BackstageLayout__body__sidebar'>
              <div className='BackstageLayout__body__sidebar__fixed'>
                <AsyncDomain
                  domain='backstage'
                  component='BackstageHeroSidebarImage'
                  key='BackstageHeroSidebarImage'
                  isRightRailActive={this.props.isRightRailActive}
                  heroImageIsCircle={this.props.heroImageIsCircle}
                  renderArtworkPlayButton={this.props.renderArtworkPlayButton}
                >
                  <Link to={this.props.heroImageLinkUrl}>
                    <HeroImageWithLogger
                      src={this.props.heroImageUrl}
                      isCircle={this.props.heroImageIsCircle}
                      useDefaultBgColor={true}
                    />
                    {this.props.heroImageHasRings && (
                      <div className='BackstageLayout__body__sidebar__image__rings'>
                        <Icon glyph={Rings} />
                      </div>
                    )}
                    {this.props.renderArtworkPlayButton && (
                      <AsyncDomain
                        domain='backstage'
                        component='BackstageLongPlayButton'
                        key='BackstageLongPlayButton'
                        playOnClick={this.props.playOnClick}
                        isArtist={this.props.heroImageIsCircle}
                      />
                    )}
                  </Link>
                </AsyncDomain>
                {this.props.isHeaderInSidebar && !this.props.isMobileWeb && (
                  <HeaderBackstageWithLogger {...this.props.headerProps} />
                )}
                {this.props.renderSidebarButtons && (
                  <div className='BackstageLayout__body__sidebar__buttons'>
                    {this.props.renderSidebarButtons()}
                  </div>
                )}
              </div>
            </aside>
            <main className='BackstageLayout__body__main'>{this.props.renderMainContent()}</main>
          </div>
          <Footer useLightTheme={true} />
        </div>
      </AppContextIntegrationData>
    );
  }
}

export function mapStateToProps(state) {
  const currentPathname = getCurrentPathnameFromState(state);
  return {
    currentPathname,
    isMobileWeb: isMobileWeb(state),
    isRightRailActive:
      getInDomain('ads.utils.getIsRightRailActive', false)(state) ||
      getInDomain('ads.utils.getIsElementCenteredWithAds', false)(state),
    mobileLongLink: getMobileLongLink(state, {
      pageUrl: currentPathname,
      feature: Constants.Urls.deepLinkParams.BACKSTAGE_PAGE,
    }),
    showLogoOnlyHeader: showLogoOnlyHeader(state),
  };
}

// eslint-disable-next-line react-redux/mapDispatchToProps-returns-object
const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      showedBackstagePage: getInDomain('backstage.actions.showedBackstagePage', noop),
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(BgSetter(BackstageLayout));
